import React, { useState, useEffect, useContext, useRef } from 'react';
import { motion } from 'framer-motion';
import { CSVLink } from 'react-csv';
import { toast } from 'react-toastify';
import axios from 'axios';
import ApiURL from '../Api/Api';
import Navbar from './Navbar';
import { SidebarContext } from './SidebarContext';
import Sidebar from './Sidebar';
import { useNavigate, useLocation } from 'react-router-dom';
import 'tailwindcss/tailwind.css';

const SpecificWorkerReport = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchDate, setSearchDate] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { isSidebarOpen = false, screenWidth = 0 } = useContext(SidebarContext) || {};
  const requestRefs = useRef({});

  const userId = localStorage.getItem('id');
  const userRole = localStorage.getItem('role') || 'worker';
  const highlightedRequestId = location.state?.highlightedRequestId;

  console.log('SpecificWorkerReport: userId=', userId, 'userRole=', userRole, 'highlightedRequestId=', highlightedRequestId);

  useEffect(() => {
    if (!userId) {
      setError('User not authenticated');
      toast.error('User not authenticated');
      navigate('/login');
      return;
    }
    fetchRequests();
  }, [userId, navigate]);

  useEffect(() => {
    const filtered = requests.filter(
      (request) =>
        (request.user_name || '').toLowerCase().includes(searchName.toLowerCase()) &&
        (request.created_at ? new Date(request.created_at).toISOString().slice(0, 10) : '').includes(searchDate)
    );
    console.log('Filtered Requests:', filtered.length, filtered);
    setFilteredRequests(filtered);

    if (highlightedRequestId && requestRefs.current[highlightedRequestId]) {
      console.log('Scrolling to request:', highlightedRequestId);
      requestRefs.current[highlightedRequestId].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [searchName, searchDate, requests, highlightedRequestId]);

  const fetchRequests = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching requests for userId:', userId, 'role:', userRole);
      const response = await axios.get(`${ApiURL}/getWorkerRequests.php`, {
        params: {
          worker_id: userRole === 'worker' ? userId : undefined,
          user_id: userId,
          role: userRole,
        },
      });
      console.log('API Response:', response.data);
      const data = Array.isArray(response.data) ? response.data : [];
      setRequests(data);
      setFilteredRequests(data);
    } catch (error) {
      const errorMessage = error.message || 'Unknown error occurred';
      setError(`Network error: ${errorMessage}`);
      toast.error(`Error fetching requests: ${errorMessage}`);
      console.error('Fetch Error:', errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Request ID',
      'Worker Name',
      'Purpose',
      'Amount',
      'Status',
      'Created Date',
      'Updated Date',
      'Updated By',
      'Rejection Reason',
    ];

    const data = filteredRequests.map((request) => ({
      'Request ID': request.id || '',
      'Worker Name': request.user_name || '',
      Purpose: request.purpose || '',
      Amount: request.amount ? request.amount.toString().replace(/[^\d.]/g, '') : '',
      Status: request.status || '',
      'Created Date': request.created_at || '',
      'Updated Date': request.updated_at || '',
      'Updated By': request.updater_name || '',
      'Rejection Reason': request.rejection_reason || '',
    }));

    return { headers, data };
  };

  return (
    <div className="min-h-screen bg-[#f0f2f8] font-sans">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0, y: 0 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`min-w-0 flex-1 min-h-screen transition-all duration-300 ${
            isSidebarOpen ? 'lg:ml-72' : ''
          } ${isSidebarOpen && screenWidth < 640 ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-3 sm:px-5 sm:py-5 lg:px-6 lg:py-6">
            <div className="mb-5 border-b border-slate-200 pb-4">
              <h1 className="text-base sm:text-lg font-bold text-slate-800 leading-tight">My Request Report</h1>
              <p className="text-[10px] sm:text-[11px] text-slate-400 mt-0.5">{filteredRequests.length} of {requests.length} requests</p>
            </div>

            <div className="mt-6">
              <form
                onSubmit={(e) => e.preventDefault()}
                className="flex flex-col sm:flex-row gap-2 mb-6 max-w-4xl mx-auto"
              >
                <input
                  type="text"
                  value={searchName}
                  onChange={(e) => setSearchName(e.target.value)}
                  placeholder="Search by Worker Name..."
                  className="flex-grow p-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm bg-white/90"
                />
                <input
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  placeholder="Search by Date"
                  className="flex-grow p-2 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 text-sm bg-white/90"
                />
                <CSVLink
                  data={exportToCSV().data}
                  headers={exportToCSV().headers}
                  filename="MyRequestReport.csv"
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 flex items-center justify-center text-sm transition-colors duration-200"
                >
                  <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Export to CSV
                </CSVLink>
              </form>

              {isLoading ? (
                <div className="text-center py-8 text-blue-600 text-sm">
                  Loading requests...
                </div>
              ) : error ? (
                <div className="text-center py-8 text-red-600 text-sm">
                  Error: {error}
                </div>
              ) : filteredRequests.length === 0 ? (
                <div className="text-center py-8 text-blue-600 text-sm">
                  No requests found
                </div>
              ) : (
                <div className="relative overflow-x-auto w-full">
                  <table className="w-full text-sm text-left text-gray-700 shadow-md rounded-lg overflow-hidden">
                    <thead className="text-sm uppercase bg-blue-200 text-black sticky top-0 z-10">
                      <tr>
                        <th className="px-2 py-1.5 sticky left-0 bg-blue-200 z-20">Request ID</th>
                        <th className="px-2 py-1.5 sticky left-16 bg-blue-200 z-20">Worker Name</th>
                        <th className="px-2 py-1.5">Purpose</th>
                        <th className="px-2 py-1.5">Amount</th>
                        <th className="px-2 py-1.5">Created Date</th>
                        <th className="px-2 py-1.5">Updated Date</th>
                        <th className="px-2 py-1.5">Updated By</th>
                        <th className="px-2 py-1.5">Rejection Reason</th>
                        <th className="px-2 py-1.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map((request) => (
                        <tr
                          key={request.id}
                          ref={(el) => (requestRefs.current[request.id] = el)}
                          className={`border-b hover:bg-blue-50 transition-colors duration-200 ${
                            request.id == highlightedRequestId
                              ? 'bg-yellow-100 border-l-4 border-yellow-500'
                              : request.status === 'approved'
                              ? 'bg-green-50'
                              : request.status === 'rejected'
                              ? 'bg-red-50'
                              : 'bg-yellow-50'
                          }`}
                        >
                          <td className="px-2 py-1.5 sticky left-0 z-10 font-medium text-blue-600 bg-inherit">
                            {request.id || 'N/A'}
                          </td>
                          <td className="px-2 py-1.5 sticky left-16 z-10 font-medium text-blue-600 bg-inherit">
                            {request.user_name || 'N/A'}
                          </td>
                          <td className="px-2 py-1.5">{request.purpose || 'N/A'}</td>
                          <td className="px-2 py-1.5">₹{request.amount || '0'}</td>
                          <td className="px-2 py-1.5">{request.created_at || 'N/A'}</td>
                          <td className="px-2 py-1.5">{request.updated_at || 'N/A'}</td>
                          <td className="px-2 py-1.5">{request.updater_name || 'N/A'}</td>
                          <td className="px-2 py-1.5">{request.rejection_reason || 'N/A'}</td>
                          <td className="px-2 py-1.5">
                            <span
                              className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                request.status === 'approved'
                                  ? 'bg-green-200 text-green-800'
                                  : request.status === 'rejected'
                                  ? 'bg-red-200 text-red-800'
                                  : 'bg-yellow-200 text-yellow-800'
                              }`}
                            >
                              {request.status
                                ? request.status.charAt(0).toUpperCase() + request.status.slice(1)
                                : 'N/A'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="sm:hidden absolute top-0 right-0 h-full w-3 bg-gradient-to-l from-gray-200/50 to-transparent pointer-events-none"></div>
                </div>
              )}
              {error && (
                <div className="mt-4 px-4 py-2 bg-red-100 text-red-600 rounded text-sm max-w-4xl mx-auto">
                  {error}
                </div>
              )}
            </div>
          </div>
        </motion.main>
        {isSidebarOpen && screenWidth < 640 && (
          <div
            className="fixed inset-0 bg-black/50 z-30"
            onClick={() => document.querySelector('button[aria-label="Close Sidebar"]').click()}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
};

export default SpecificWorkerReport;
